import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api, type Memo, type Lookup } from '../lib/api'
import { today } from '../lib/format'
import { DEFAULT_MEMO_TERMS } from '../lib/memoTerms'
import { Panel, Field, Alert, Loading, Tag } from '../components/ui'
import Combo from '../components/Combo'

/** 範本《文案撰寫外包合作備忘錄》的預設條款參數 */
const BLANK = {
  writer_id: 0,
  party_b_title: '',
  rep_name: '',
  address: '',
  phone: '',
  id_no: '',
  effective_from: today(),
  effective_to: '',
  signed_on: today(),
  revision_limit: 2,
  settle_day: 25,
  withhold_tax_pct: 10,
  supplement_pct: 2.11,
  terms: DEFAULT_MEMO_TERMS,
  note: '',
}

export default function MemoForm() {
  const { id } = useParams()
  const nav = useNavigate()
  const [form, setForm] = useState({ ...BLANK })
  const [existing, setExisting] = useState<Memo | null>(null)
  const [lookup, setLookup] = useState<Lookup | null>(null)
  const [loaded, setLoaded] = useState(!id)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [role, setRole] = useState('')

  const isAdmin = role === 'admin'
  const isDraft = !existing || existing.status === '草稿'
  const editable = isDraft || isAdmin

  useEffect(() => {
    api.get<Lookup>('/masters/lookup').then(setLookup).catch(() => {})
    api.get<{ user: { role: string } }>('/me').then((d) => setRole(d.user.role)).catch(() => {})
  }, [])

  useEffect(() => {
    if (!id) return
    api
      .get<Memo>(`/memos/${id}`)
      .then((m) => {
        setExisting(m)
        setForm({
          writer_id: m.writer_id,
          party_b_title: m.party_b_title ?? '',
          rep_name: m.rep_name ?? '',
          address: m.address ?? '',
          phone: m.phone ?? '',
          id_no: m.id_no ?? '',
          effective_from: m.effective_from ?? '',
          effective_to: m.effective_to ?? '',
          signed_on: m.signed_on ?? '',
          revision_limit: m.revision_limit,
          settle_day: m.settle_day,
          withhold_tax_pct: m.withhold_tax_pct,
          supplement_pct: m.supplement_pct,
          terms: m.terms ?? '',
          note: m.note ?? '',
        })
        setLoaded(true)
      })
      .catch((e) => { setError(e.message); setLoaded(true) })
  }, [id])

  /** 選寫手時順手把乙方名稱帶好，多數情況不用再打一次 */
  const pickWriter = (writerId: number) => {
    const w = lookup?.writers.find((x) => x.id === writerId)
    setForm((f) => ({
      ...f,
      writer_id: writerId,
      party_b_title: f.party_b_title || w?.name || '',
    }))
  }

  /** 回傳存檔後的備忘錄 id；silent 用在「預覽列印」那條路，不要跳存檔成功的提示 */
  const save = async ({ review = false, silent = false } = {}): Promise<number | null> => {
    setError('')
    setSaving(true)
    try {
      if (!form.writer_id) throw new Error('請選擇寫手')
      if (!form.effective_to) throw new Error('請填寫合約期限的迄日，否則不會出現到期提醒')
      const res = id
        ? await api.put<Memo>(`/memos/${id}`, form)
        : await api.post<Memo>('/memos', { ...form, submit_for_review: review })
      if (!id && review) {
        alert(`已送出「${res.writer_name}」的備忘錄審核，等待管理員核准`)
        nav('/memos')
        return null
      }
      nav(`/memos/${res.id}`, { replace: true })
      setExisting(res)
      if (!silent) alert(id ? '已儲存變更' : `已儲存草稿`)
      return res.id
    } catch (e: any) {
      setError(e.message)
      return null
    } finally {
      setSaving(false)
    }
  }

  /**
   * 開列印預覽。列印與下載 PDF 都在預覽頁上操作，這裡只負責把人帶過去。
   *
   * 列印頁的網址要 id，新增中的備忘錄還沒有，所以先存成草稿再開；
   * 已存在的也先存一次，避免預覽到的是舊內容。
   *
   * 分頁必須在點擊當下同步開啟：等 await 結束再 window.open 會失去使用者手勢，
   * 被瀏覽器當成彈出式廣告擋掉。所以先開空白分頁佔位，存檔完成後再導向。
   */
  const openPrint = async () => {
    const win = window.open('', '_blank')
    let targetId: number | null = existing?.id ?? (id ? Number(id) : null)
    if (editable) {
      targetId = await save({ silent: true })
    }
    if (!targetId) {
      win?.close()          // 存檔失敗（例如漏填寫手）就別留一個空白分頁
      return
    }
    const url = `/memos/${targetId}/print`
    if (win) win.location.href = url
    else window.location.href = url    // 分頁被擋掉時退而求其次，直接在本頁開
  }

  /** 既有草稿：先存檔再送審，避免送出的是舊內容 */
  const submitExisting = async () => {
    if (!existing) return
    if (!confirm(`確定送出「${existing.writer_name}」的備忘錄審核？\n\n目前未儲存的變更會一併存檔。`)) return
    setError('')
    setSaving(true)
    try {
      await api.put<Memo>(`/memos/${existing.id}`, form)
      const res = await api.post<Memo>(`/memos/${existing.id}/submit`)
      setExisting(res)
      alert('已送出審核，等待管理員核准')
    } catch (e: any) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  if (!loaded) return <Loading />

  const set = (patch: Partial<typeof form>) => setForm({ ...form, ...patch })

  return (
    <>
      <div className="page-head">
        <h2>{existing ? `合作備忘錄 — ${existing.writer_name}` : '新增合作備忘錄'}</h2>
        {existing && <Tag value={existing.status} />}
        {existing && !editable && <span className="hint">已送審，欄位唯讀</span>}
        <div className="spacer" />
        <div className="btn-row">
          <button onClick={() => openPrint()} disabled={saving}>預覽列印</button>
          {existing && <Link to="/memos"><button>返回清單</button></Link>}
          {editable && (
            <button onClick={() => save()} disabled={saving}>
              {saving ? '儲存中…' : existing ? '儲存變更' : '儲存草稿'}
            </button>
          )}
          {!existing && (
            <button className="primary" onClick={() => save({ review: true })} disabled={saving}>
              {saving ? '處理中…' : '送出審核'}
            </button>
          )}
          {existing && existing.status === '草稿' && (
            <button className="primary" onClick={submitExisting} disabled={saving}>
              {saving ? '處理中…' : '送出審核'}
            </button>
          )}
        </div>
      </div>

      <Alert kind="error">{error}</Alert>

      {existing && existing.status === '草稿' && existing.review_note && (
        <Alert kind="warn">
          此備忘錄於 {existing.reviewed_at} 被 {existing.reviewed_by} 退回：{existing.review_note}
          　修改後請再次「送出審核」。
        </Alert>
      )}

      <fieldset className="form-lock" disabled={!editable}>
        <Panel title="一、乙方資料">
          <div className="grid c4">
            <Field label="寫手（乙方）">
              {/* 跟發包單一致：打字搜尋，中文名或英文名都找得到 */}
              <Combo
                items={lookup?.writers ?? []}
                value={form.writer_id || null}
                placeholder="輸入寫手姓名"
                allowEmpty={false}
                search={(w) => `${w.name} ${w.name_en ?? ''}`}
                sub={(w) => `[${w.lang}]`}
                onChange={(w) => pickWriter(w ? w.id : 0)}
              />
            </Field>
            <Field label="乙方名稱（合約全銜）">
              <input value={form.party_b_title} onChange={(e) => set({ party_b_title: e.target.value })} />
            </Field>
            <Field label="簽約代表人">
              <input value={form.rep_name} onChange={(e) => set({ rep_name: e.target.value })} />
            </Field>
            <Field label="身分證字號／統一編號">
              <input value={form.id_no} onChange={(e) => set({ id_no: e.target.value })} />
            </Field>
            <Field label="電話">
              <input value={form.phone} onChange={(e) => set({ phone: e.target.value })} />
            </Field>
            <Field label="地址" span={3}>
              <input value={form.address} onChange={(e) => set({ address: e.target.value })} />
            </Field>
          </div>
        </Panel>

        <Panel title="二、合約期限">
          <div className="grid c4">
            <Field label="簽約日期">
              <input type="date" value={form.signed_on} onChange={(e) => set({ signed_on: e.target.value })} />
            </Field>
            <Field label="生效日">
              <input type="date" value={form.effective_from} onChange={(e) => set({ effective_from: e.target.value })} />
            </Field>
            <Field label="到期日">
              <input type="date" value={form.effective_to} onChange={(e) => set({ effective_to: e.target.value })} />
            </Field>
            <Field label="修改次數上限（第一條）">
              <input
                type="number"
                value={form.revision_limit}
                onChange={(e) => set({ revision_limit: Number(e.target.value) })}
              />
            </Field>
          </div>
        </Panel>

        <Panel title="三、條款參數">
          <p className="hint" style={{ margin: '0 0 8px', fontSize: 12 }}>
            這些數字只是內部備查。列印出來的條款以下方「條款正文」為準，
            要改實際條件請直接改正文。
          </p>
          <div className="grid c4">
            <Field label="每月結算日">
              <input type="number" value={form.settle_day} onChange={(e) => set({ settle_day: Number(e.target.value) })} />
            </Field>
            <Field label="扣繳稅率 %">
              <input
                type="number" step="0.01"
                value={form.withhold_tax_pct}
                onChange={(e) => set({ withhold_tax_pct: Number(e.target.value) })}
              />
            </Field>
            <Field label="補充保險費 %">
              <input
                type="number" step="0.01"
                value={form.supplement_pct}
                onChange={(e) => set({ supplement_pct: Number(e.target.value) })}
              />
            </Field>
          </div>
        </Panel>

        <Panel title="四、條款正文">
          <p className="hint" style={{ margin: '0 0 8px', fontSize: 12 }}>
            已帶入《文案撰寫外包合作備忘錄》原文。這份文字會逐份存檔並原樣列印，
            改這裡只影響這一份，不會動到已經簽出去的舊合約。
            <code>{'{{匯款帳戶}}'}</code> 這一行列印時會換成下方填的帳戶表格，請保留。
          </p>
          <textarea
            rows={18}
            value={form.terms}
            onChange={(e) => set({ terms: e.target.value })}
            placeholder="第一條 標的&#10;……"
          />
        </Panel>

        <Panel title="備註">
          <textarea rows={3} value={form.note} onChange={(e) => set({ note: e.target.value })} />
        </Panel>
      </fieldset>
    </>
  )
}
